<template>
  <div
    v-bind:class='["what-app-message", { light: isMine }]'
  >
    <h5 class="what-app-message__author">{{ author }}</h5>
    <div class="what-app-message__content">
      <div class="what-app-message__column">
        <div
          v-if="quotedMessage"
          class="what-app-message__reply"
        >
          <span class="what-app-message__reply__caption">Цитируемое сообщение:</span>
          <span class="what-app-message__reply__text">{{ quotedMessage }}</span>
        </div>
        <div class="what-app-message__text">{{ text }}</div>
        <a
          v-if="isDocument"
          v-bind:href="downloadUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ fileName }}
        </a>
        <a
          v-if="isImage || isSticker"
          v-bind:href="downloadUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="what-app-message__image"
        >
          <img v-bind:src="downloadUrl" alt="foto" />
        </a>
        <span
          v-if="isSpecificTypeMessageText"
        >
          {{ isSpecificTypeMessageText }}
        </span>
      </div>
      <v-menu open-on-click class="what-app-message__action">
        <template v-slot:activator="{ on }">
          <button
            type="button"
            v-on="on"
            class="what-app-message__action__tooltip"
          />
        </template>
        <div class="what-app-message__action__buttons">
          <button
            v-if="isMine"
            type="button"
            v-on:click="onDelete"
            class="what-app-message__action__button delete"
          >
            Удалить
          </button>
          <button
            type="button"
            v-on:click="onReply"
            class="what-app-message__action__button reply"
          >
            Ответить
          </button>
        </div>
      </v-menu>
    </div>
  </div>
</template>

<script>

/**
 * Типы сообщений
 */
const MESSAGE_TYPE_IMAGE = 'imageMessage';
const MESSAGE_TYPE_DOCUMENT = 'documentMessage';
const MESSAGE_TYPE_AUDIO = 'audioMessage';
const MESSAGE_TYPE_VIDEO = 'videoMessage';
const MESSAGE_TYPE_STICKER = 'stickerMessage';
const MESSAGE_TYPE_LOCATION = 'locationMessage';
const MESSAGE_TYPE_CONTACT = 'contactMessage';
const MESSAGE_TYPE_POLL = 'pollMessage';
const MESSAGE_TYPE_POLL_UPDATE = 'pollUpdateMessage';

/**
 * @description Название события удаления сообщения.
 * @type {string}
 */
const NAME_EVENT_DELETE = 'delete';

/**
 * @description Название события ответа на сообщение.
 * @type {string}
 */
const NAME_EVENT_REPLY = 'reply';

/**
 * @description Название события пересылки сообщения.
 * @type {string}
 */
const NAME_EVENT_FORWARD = 'forward';

/**
 * @description Компонент Whats App сообщения.
 * @type {Vue}
 */
export default {
  computed: {
    isImage() {
      return this.type === MESSAGE_TYPE_IMAGE;
    },
    isDocument() {
      return this.type === MESSAGE_TYPE_DOCUMENT;
    },
    isSpecificTypeMessageText() {
      if (this.type === MESSAGE_TYPE_VIDEO) {
        return 'Видео сообщение, просмотреть можно в мобильной версии WA';
      }
      if (this.type === MESSAGE_TYPE_AUDIO) {
        return 'Голосовое сообщение, просмотреть можно в мобильной версии WA';
      }
      if (this.type === MESSAGE_TYPE_LOCATION) {
        return 'Сообщение геолокации, просмотреть можно в мобильной версии WA';
      }
      if (this.type === MESSAGE_TYPE_CONTACT) {
        return 'Сообщение контакта, просмотреть можно в мобильной версии WA';
      }
      if (this.type === MESSAGE_TYPE_POLL || this.type === MESSAGE_TYPE_POLL_UPDATE) {
        return 'Сообщение опроса, просмотреть можно в мобильной версии WA';
      }
      return '';
    },
    isSticker() {
      return this.type === MESSAGE_TYPE_STICKER;
    },
  },
  methods: {
    /**
     * @description Обработать событие удаления.
     * @type {string}
     */
    onDelete() {
      this.$emit(NAME_EVENT_DELETE);
    },
    /**
     * @description Обработать событие ответа на сообщение.
     * @type {string}
     */
    onReply() {
      this.$emit(NAME_EVENT_REPLY);
    },
    /**
     * @description Обработать событие пересылки сообщения.
     * @type {string}
     */
    onForward() {
      this.$emit(NAME_EVENT_FORWARD);
    },
  },
  name: 'WhatsAppMessage',
  props: {
    /**
     * @description Текст сообщения.
     * @type {String}
     */
    text: {
      required: true,
      type: String,
    },
    /**
     * @description Автор сообщения.
     * @type {String}
     */
    author: {
      required: true,
      type: String,
    },
    /**
     * @description Отправлено клиникой.
     * @type {Boolean}
     */
    isMine: {
      required: true,
      type: Boolean,
    },
    /**
     * @description Цитируемое сообщение.
     * @type {?String}
     */
    quotedMessage: {
      default: () => null,
      type: String,
    },
    /**
     * @description Ссылка на файл.
     * @type {String}
     */
    downloadUrl: {
      default: () => null,
      type: String,
    },
    /**
     * @description Название файла.
     * @type {String}
     */
    fileName: {
      default: () => null,
      type: String,
    },
    /**
     * @description Тип сообщения.
     * @type {String}
     */
    type: {
      required: true,
      type: String,
    },
  },
};
</script>

<style scoped lang="scss">
.what-app-message {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-self: flex-start;
  width: fit-content;
  word-break: break-all;
  &.right {
    align-self: flex-end;
  }
  &.light {
    .what-app-message__content {
      background-color: #eaf4dd;
    }
  }
  &__author {
    font-size: 16px;
    line-height: 19px;
    font-weight: 700;
    color: #192A3E;
  }
  &__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 4px;
    padding: 13px;
    background-color: #EAEFF3;
    position: relative;
    cursor: pointer;
    &:hover {
      .what-app-message__action__tooltip:after {
        background-image: url("@/assets/img/points.svg");
        background-position: right;
      }
    }
  }
  &__action {
    position: relative;
    &__tooltip {
      display: flex;
      align-items: center;
      &:after {
        content: '';
        width: 30px;
        height: 20px;
      }
    }
    &__buttons {
      display: flex;
      flex-direction: column;
      align-items: self-start;
      padding: 15px 0 15px 0;
      background: #FFFFFF;
      box-shadow: -1px 3px 7.1px rgba(142, 141, 208, 0.13);
    }
    &__button {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 16px;
      font-weight: 400;
      padding: 10px 50px 10px 15px;
      width: 100%;
      &:hover {
        background-color: #EAEFF3;
      }
      &:before {
        content: '';
        background-position: center;
        width: 20px;
        height: 20px;
      }
      &.delete:before {
        background-image: url("@/assets/img/exit-grey-min.svg");
      }
      &.reply:before {
        background-image: url("@/assets/img/share-grey.svg");
      }
      &.forward:before {
        background-image: url("@/assets/img/forward-grey.svg");
      }
    }
  }
  &__column {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  &__text {
    word-break: keep-all;
    font-size: 16px;
    line-height: 19px;
    font-weight: 400;
  }
  &__reply {
    display: flex;
    flex-direction: column;
    gap: 5px;
    background-color: rgba(4, 137, 224, 0.2);
    padding: 5px 20px;
    border-radius: 6px;
    border-left: 4px solid #0489E0;
    font-size: 14px;
    line-height: 16px;
    &__caption {
      color: #0489E0;
    }
    &__text {
      color: rgba(112, 118, 131, 1);
      display: block;
    }
  }
  &__image {
    max-width: 250px;
    height: 250px;
    img {
      width: 100%;
      height: 100%;
      border-radius: 4px;
    }
  }
}
@media screen and (max-width: 860px) {
  .what-app-message {
    &__action {
      &__tooltip {
        &:after {
          background-image: url("@/assets/img/points.svg");
          background-position: right;
        }
      }
    }
  }
}
</style>
